"use client"

import { isEqual } from "lodash-es"
import { useEffect } from "react"
import { withPosthogWeb } from "src/integrations/posthog/client-web"
import { posthogDebug } from "src/integrations/posthog/debug"
import { createPersistentStore } from "src/utilities/localStorage/create"
import { ExperimentName, experiments, ExperimentVariant } from "./experiments"

type Flags = Partial<Record<ExperimentName, ExperimentVariant<ExperimentName>>>

type State = {
  loading: boolean
  flags: Flags
}

const defaultState: State = {
  loading: true,
  flags: Object.keys(experiments).reduce<Flags>((result, key) => {
    result[key as ExperimentName] = "control"
    return result
  }, {}),
}

const store = createPersistentStore(() => defaultState, {
  name: "experiments",
  version: 2,
  partialize: (state) => {
    return {
      flags: state.flags,
    }
  },
})

export function getFeatureFlagsState() {
  return store.getState()
}

export function updateFeatureFlags() {
  withPosthogWeb((posthog) => {
    const details = posthog.featureFlags.getFlagsWithDetails()

    if (!Object.keys(details).length) {
      return
    }

    const newState = {
      loading: false,
      flags: {
        ...defaultState.flags,
        ...Object.keys(experiments).reduce<Flags>((result, name) => {
          result[name as ExperimentName] = (details[name]?.variant as any) ?? "control"
          return result
        }, {}),
      },
    }

    if (!isEqual(store.getState(), newState)) {
      posthogDebug("feature flags updated", store.getState(), "=>", newState)
      store.setState(newState)
    }
  })
}

/** Calls the provided function as soon as PostHog feature flags are available. */
export function withPosthogFeatureFlags(callback: () => void) {
  if (!getFeatureFlagsState().loading) {
    callback()
  } else {
    const interval = setInterval(() => {
      if (!getFeatureFlagsState().loading) {
        clearInterval(interval)
        callback()
      }
    }, 100)
  }
}

export function useExperiment<Name extends ExperimentName>(name: Name) {
  const loading = store((state) => state.loading)
  const variant = store((state) => (state.flags[name] as ExperimentVariant<Name>) || "control")

  useEffect(() => {
    withPosthogWeb((posthog) => {
      // track exposure of the experiment variant
      posthogDebug("experiment exposure", name, variant)
      posthog?.getFeatureFlag(name)
    })
  }, [name, variant])

  return { loading, variant }
}

export function setLocalExperimentVariant<Name extends ExperimentName>(name: Name, value: ExperimentVariant<Name>) {
  withPosthogWeb((posthog) => {
    posthog.featureFlags.overrideFeatureFlags({ [name]: value || "control" })
    store.setState({ flags: { ...store.getState().flags, [name]: value } })
  })
}
